import { useEffect, useState, useCallback } from 'react';
import {
  DEFAULT_FEATURE_FLAGS,
  FeatureFlags,
  fetchFeatureFlags,
} from 'src/services/featureFlagsService';

/**
 * Custom hook for managing feature flags
 * 
 * Features:
 * - Fetches feature flags from backend API
 * - Handles loading state to prevent UI flicker
 * - Caches flags to avoid duplicate API calls within the same session
 * - Falls back to default values (Spectro OFF) if API fails
 * - Provides refetch capability for manual updates
 * 
 * @returns {Object} Object containing:
 *   - flags: The feature flags object or null during initial load
 *   - loading: Loading state indicator
 *   - error: Error state if fetch failed
 *   - refetch: Function to manually refetch flags
 */
export const useFeatureFlags = (isAuthenticated: boolean) => {
  const [flags, setFlags] = useState<FeatureFlags | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFlags = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    }
    setError(null);
    try {
      const featureFlags = await fetchFeatureFlags();
      setFlags(featureFlags);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setFlags(DEFAULT_FEATURE_FLAGS);
    } finally {
      if (isInitial) {
        setLoading(false);
      }
    }
  }, []);

  const refetch = useCallback(() => fetchFlags(false), [fetchFlags]);

  // Fetch flags only once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchFlags(true);
    } else {
      setFlags(null);
      setLoading(true);
    }
  }, [isAuthenticated, fetchFlags]);

  return {
    flags,
    loading,
    error,
    refetch,
  };
};
